<template>
  <v-col>
    <v-row>
      <v-col class="col-auto">
        <v-row>
          <v-col>
            <span class="subtitle-2 font-weight-bold">User Information</span>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span class="caption">View and manage your profile and account settings.</span>
          </v-col>
        </v-row>
      </v-col>

      <v-spacer />

      <v-col
        v-if="currentUser.hasAccessToUpdatePassword"
        class="col-auto"
      >
        <v-btn
          color="primary elevation-0"
          outlined
          @click="promptToUpdatePassword"
        >
          <span class="caption">Update Password</span>
        </v-btn>

        <v-dialog
          v-model="showViewToUpdatePassword"
          max-width="500"
        >
          <v-card>
            <v-card-title>
              <span class="subtitle-2 font-weight-bold">Update Password</span>
            </v-card-title>

            <v-card-text>
              <v-form>
                <v-text-field
                  v-model="inputPassword"
                  label="Password"
                  required
                  type="password"
                />
              </v-form>
            </v-card-text>

            <v-card-actions>
              <!-- Cancel -->
              <v-btn
                color="primary"
                text
                @click="cancelUpdatePassword"
              >
                Cancel
              </v-btn>

              <!-- "Update Password" Button -->
              <v-btn
                color="primary"
                @click="updateAuthPassword"
              >
                Update Password
              </v-btn>
            </v-card-actions>

            <!-- "Update Password" Error/Success Messages -->
            <v-card-text>
              <v-row>
                <v-col>
                  <!-- Error Message -->
                  <span
                    v-if="errorUpdatingPassword"
                  >{{ errorUpdatingPassword }}</span>

                  <!-- Success Message -->
                  <span
                    v-if="successUpdatingPassword"
                  >Password updated successfully.</span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <!-- Divider -->
    <v-row>
      <v-col>
        <v-divider />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <span class="subtitle-2 font-weight-bold">Profile</span>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Name</span>
          </v-col>

          <v-col class="col-auto pl-0">
            <!-- Text Button: Add Name -->
            <v-btn
              color="primary elevation-0"
              text
              x-small
              @click="promptToUpdateName"
            >
              <span class="caption">Edit</span>
            </v-btn>

            <v-dialog
              v-model="showViewToUpdateName"
              max-width="500"
            >
              <v-card>
                <v-card-title>
                  <v-col>
                    <span class="subtitle-2 font-weight-bold">Update Name</span>
                  </v-col>

                  <v-spacer />

                  <v-col class="col-auto">
                    <v-btn
                      icon
                      @click="cancelUpdateName"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'times']"
                        size="lg"
                      />
                    </v-btn>
                  </v-col>
                </v-card-title>

                <v-card-text>
                  <v-col>
                    <v-row>
                      <v-col class="pa-0">
                        <v-form>
                          <v-col>
                            <v-row>
                              <v-col>
                                <v-text-field
                                  v-model="inputNameFirst"
                                  label="First Name"
                                  required
                                  outlined
                                  :rules="[
                                    v => !!v || 'First name is required',
                                    v => /^[a-zA-Z\s-'’]+$/.test(v) || 'First name must contain only letters, spaces, hyphens, or apostrophes'
                                  ]"
                                />
                              </v-col>
                            </v-row>

                            <v-row>
                              <v-col>
                                <v-text-field
                                  v-model="inputNameLast"
                                  label="Last Name"
                                  required
                                  outlined
                                  :rules="[
                                    v => !!v || 'Last name is required',
                                    v => /^[a-zA-Z\s-'’]+$/.test(v) || 'Last name must contain only letters, spaces, hyphens, or apostrophes'
                                  ]"
                                />
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-form>
                      </v-col>
                    </v-row>

                    <!-- Error Message -->
                    <v-row v-if="errorUpdatingName">
                      <v-col>
                        <span class="error--text">{{ errorUpdatingName }}</span>
                      </v-col>
                    </v-row>

                    <!-- Success Message -->
                    <v-row v-if="successUpdatingName">
                      <v-col>
                        <span class="success--text">Name updated successfully.</span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-card-text>

                <v-card-actions>
                  <v-col class="col-auto">
                    <!-- Cancel -->
                    <v-btn
                      color="primary"
                      text
                      @click="cancelUpdateName"
                    >
                      Cancel
                    </v-btn>
                  </v-col>

                  <v-spacer />

                  <v-col class="col-auto">
                    <!-- "Update Name" Button -->
                    <v-btn
                      color="primary"
                      @click="updatePersonName"
                    >
                      Update
                    </v-btn>
                  </v-col>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span class="caption font-weight-medium">{{ currentUser.personNameFirst }} </span>
            <span class="caption font-weight-medium">{{ currentUser.personNameLast }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Email</span>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span class="caption font-weight-medium">{{ currentUser.email }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Phone Number</span>
          </v-col>

          <v-col class="col-auto pl-0">
            <!-- Text Button: Add Phone Number -->
            <v-btn
              color="primary elevation-0"
              text
              x-small
              @click="promptToAddPhoneNumber"
            >
              <span class="caption">{{ currentUser.phoneNumber ? 'Edit' : 'Add' }}</span>
            </v-btn>

            <v-dialog
              v-model="showViewToUpdatePhoneNumber"
              max-width="500"
            >
              <v-card>
                <v-card-title>
                  <v-col class="col-auto">
                    <span class="subtitle-2 font-weight-bold">Update Phone Number</span>
                  </v-col>

                  <v-spacer />

                  <v-col class="col-auto">
                    <v-btn
                      icon
                      @click="cancelAddPhoneNumber"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'times']"
                        size="lg"
                      />
                    </v-btn>
                  </v-col>
                </v-card-title>

                <v-card-text>
                  <v-col>
                    <v-row>
                      <v-col>
                        <v-form>
                          <v-text-field
                            v-model="inputPhoneNumber"
                            label="Work Number"
                            required
                            type="tel"
                            placeholder="+1234567890"
                            :rules="[
                              v => !!v || 'Phone number is required',
                              v => /^\+?[0-9]\d{1,14}$/.test(v) || 'Invalid phone number format'
                            ]"
                          />
                        </v-form>
                      </v-col>
                    </v-row>

                    <!-- Error Message -->
                    <v-row v-if="errorAddingPhoneNumber">
                      <v-col>
                        <span class="error--text">{{ errorAddingPhoneNumber }}</span>
                      </v-col>
                    </v-row>

                    <!-- Success Message -->
                    <v-row v-if="successAddingPhoneNumber">
                      <v-col>
                        <span class="success--text">Phone number added successfully.</span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-card-text>

                <v-card-actions>
                  <v-col class="col-auto">
                    <!-- Cancel -->
                    <v-btn
                      color="primary"
                      text
                      @click="cancelAddPhoneNumber"
                    >
                      Cancel
                    </v-btn>
                  </v-col>

                  <v-spacer />

                  <v-col class="col-auto">
                    <!-- "Update Phone Number" Button -->
                    <v-btn
                      color="primary"
                      @click="addPhoneNumber"
                    >
                      Update
                    </v-btn>
                  </v-col>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>

        <v-row
          v-if="currentUser.phoneNumber"
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-medium">{{ currentUser.phoneNumber }}</span>
          </v-col>
        </v-row>

        <v-row
          v-else
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-medium">Not provided</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-divider />
      </v-col>
    </v-row>

    <v-row>
      <v-col class="col-auto">
        <span class="subtitle-2 font-weight-bold">Permissions</span>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Role</span>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span class="caption font-weight-medium">{{ currentUser.access.role === 'internal' ? 'Internal' : 'External' }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Level</span>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span class="caption font-weight-medium">{{ currentUser.access.level }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Custom Roles</span>
          </v-col>
        </v-row>

        <v-row
          v-if="currentUser.hasCustomRoleAdmin"
          class="mt-0"
        >
          <v-col>
            <span class="caption font-weight-medium">Admin</span>
          </v-col>
        </v-row>

        <v-row
          v-if="currentUser.hasCustomRoleDev"
          class="mt-0"
        >
          <v-col :class="`${currentUser.hasCustomRoleAdmin ? 'pt-0' : ''}`">
            <span class="caption font-weight-medium">Developer</span>
          </v-col>
        </v-row>

        <v-row v-else-if="!currentUser.hasCustomRoleAdmin && !currentUser.hasCustomRoleDev">
          <v-col>
            <span class="caption font-weight-medium">No custom roles assigned</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters, mapState: mapAuthState } = createNamespacedHelpers('auth')

export default {
  name: 'AdminProfile',
  data() {
    return {
      inputPassword: '',
      errorUpdatingPassword: '',
      successUpdatingPassword: false,
      showViewToUpdatePassword: false,

      inputPhoneNumber: '',
      errorAddingPhoneNumber: '',
      successAddingPhoneNumber: false,
      showViewToUpdatePhoneNumber: false,

      inputNameFirst: '',
      inputNameLast: '',
      errorUpdatingName: '',
      successUpdatingName: false,
      showViewToUpdateName: false,
    }
  },
  computed: {
    ...mapAuthGetters([
      'currentUser',
      'isUserInternalDev',
    ]),
    ...mapAuthState(['user']),
    hasAccessToUpdatePassword() {
      return this.currentUser.hasAccessToUpdatePassword || false
    },
  },
  watch: {
    currentUser(/* newUser */) {
      // console.log('Current user updated:', newUser)
    },
  },
  methods: {
    promptToUpdatePassword() {
      this.showViewToUpdatePassword = true
    },
    cancelUpdatePassword() {
      this.showViewToUpdatePassword = false
      this.inputPassword = ''
    },
    async updateAuthPassword() {
      try {
        await this.$store.dispatch('auth/updatePassword', this.inputPassword)
        this.successUpdatingPassword = true
      } catch (error) {
        this.errorUpdatingPassword = error.message
      }
    },

    promptToAddPhoneNumber() {
      this.inputPhoneNumber = ''
      this.showViewToUpdatePhoneNumber = true
    },

    cancelAddPhoneNumber() {
      this.showViewToUpdatePhoneNumber = false
      this.inputPhoneNumber = ''
    },

    async addPhoneNumber() {
      try {
        const result = await this.$store.dispatch('auth/addPhoneNumber', this.inputPhoneNumber)

        if (result.error) {
          this.errorAddingPhoneNumber = result.message || 'Failed to add phone number.'
        } else {
          this.successAddingPhoneNumber = true
          this.errorAddingPhoneNumber = ''
          this.inputPhoneNumber = ''

          // Do not close the dialog automatically
          // so that the user can see the success message.
          // this.showViewToUpdatePhoneNumber = false
        }
      } catch (error) {
        console.error('Error adding phone number:', error)
      }
    },

    promptToUpdateName() {
      this.inputNameFirst = this.currentUser.personNameFirst || ''
      this.inputNameLast = this.currentUser.personNameLast || ''

      this.showViewToUpdateName = true
    },
    cancelUpdateName() {
      this.showViewToUpdateName = false

      this.inputNameFirst = this.currentUser.personNameFirst || ''
      this.inputNameLast = this.currentUser.personNameLast || ''
    },
    async updatePersonName() {
      try {
        const { access, email, managerPersonId, personId } = this.currentUser
        const action_type = 'UPDATE'
        const person = {
          action_type,
          access,
          managerPersonId,
          email: email.trim(),
          personId,
          personNameFirst: this.inputNameFirst.trim(),
          personNameLast: this.inputNameLast.trim(),
        }

        const result = await this.$store.dispatch('admin/updatePerson', person, { root: true })

        if (!result) {
          this.errorUpdatingName = 'Unknown error occurred while updating name.'
        } else if (result.error) {
          this.errorUpdatingName = result.message || 'Failed to update name.'
        } else {
          this.errorUpdatingName = ''
          this.successUpdatingName = true

          // Do not close the dialog automatically
          // so that the user can see the success message.
          // this.showViewToUpdateName = false

          // Update the current user on the page.
          this.$store.commit('auth/user', {
            ...this.user,
            personNameFirst: this.inputNameFirst.trim(),
            personNameLast: this.inputNameLast.trim(),
          }, { root: true })

          // Update the person collection.
          const reportKey = 'person'

          this.$store.dispatch('tools/setView', reportKey, { root: true })
        }
      } catch (error) {
        this.errorUpdatingName = error.message || 'An error occurred while updating name.'
      }
    },
  },
}
</script>
